import React, { useEffect, useCallback, useState, useRef } from 'react';
import ReactDom from 'react-dom';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, EnquirySearchFilters } from 'types';
import { MD } from 'appConstants/layout';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';

import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import PopUp, { PopUpOutFields } from './popup';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import {
  queryFeatureLayerExtentStart,
  clearFeatureLayerLayerExtent
} from 'redux/map/actions';
import { fetchEnquiryById } from 'redux/enquiries';
import { useComponentMount } from 'utilities/hooks.util';
import Utils from 'utilities/helper.util';
import { addLayerToMap, findLayerById } from 'utilities/map-instance.util';

const filterStringReducer = (accumulator: any, currentValue: any) => {
  const [key, value] = currentValue;

  const query = accumulator ? `${accumulator} AND ` : '';
  switch (key) {
    case 'createdAfter':
      return value
        ? `${query}created_at > TIMESTAMP '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}'`
        : accumulator;
    case 'createdBefore':
      return value
        ? `${query}created_at < TIMESTAMP '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}'`
        : accumulator;
    case 'digStartAfter':
      return value
        ? `${query}dig_start_at > DATE '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD')}'`
        : accumulator;
    case 'digStartBefore':
      return value
        ? `${query}dig_start_at < DATE '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD')}'`
        : accumulator;
    case 'status':
      return value.length > 0
        ? `${query}status IN (${value.map((s: string) => `'${s}'`).join(',')})`
        : accumulator;
    case 'userReference':
      return value
        ? `${query}UPPER(user_reference) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'address':
      return value
        ? `${query}UPPER(address) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'isAutoRenewPending':
      return value
        ? `${query}((is_auto_renew_complete <> true OR is_auto_renew_complete IS NULL) AND next_auto_renew_at IS NOT NULL)`
        : accumulator;
    case 'requestorFullName':
      return value
        ? `${query}UPPER(user_full_name) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'requestorEmail':
      return value
        ? `${query}UPPER(user_email) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'isSandboxTest':
      return !value ? `${query}is_sandbox_test = ${value}` : accumulator;
    case 'externalId':
      return value ? `${query}external_id = ${value}` : accumulator;
    case 'tenantId':
      return value ? `${query}tenant_id = ${value}` : accumulator;
    default:
      return accumulator;
  }
};

const MapView: React.FC<Props> = ({
  isFullSet,
  searchFilter,
  enquiriesLayerExtent,
  selectedId,
  user,
  baseMapTheme,
  isPanelExpanded,
  isMapVisible,
  mapSearchWithExtentZoom
}) => {
  const mapSearchWithExtentZoomRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const featureLayerRef = useRef<any>(null);

  const extraMapModules = [
    MapSettings.modules.featureLayer,
    MapSettings.modules.polygon
  ];
  const dispatch: any = useDispatch();

  const clickDetailButton = (enquiry: any) => {
    const { loadedView } = window;
    loadedView.closePopup();
    selectedId(Number(enquiry.external_id));
    dispatch(
      fetchEnquiryById({ enquiryId: Number(enquiry.objectid), logo: true })
    );
  };

  const onMapReady = () => {
    const { loadedView, loadedMap, loadedMapModules } = window;
    if (loadedMap) {
      const { Config, FeatureLayer } = loadedMapModules;
      Config.request.interceptors.push({
        urls: getFeatureServerUrl('Enquiries'),
        before: featureRequestInterceptors
      });

      const popupTemplate = {
        content: (feature: any) => {
          const node = document.createElement('div');
          ReactDom.render(
            <PopUp
              isFullSet={isFullSet}
              attributes={feature?.graphic?.attributes || null}
              geometry={feature?.graphic?.geometry}
              onButtonClick={clickDetailButton}
            />,
            node
          );
          return node;
        },
        outFields: PopUpOutFields,
        returnGeometry: true,
        overwriteActions: true
      };

      // Check if layer already exists in the map or in our ref
      let featureLayer: any = findLayerById('enquiries-feature-layer');

      if (!featureLayer && !featureLayerRef.current) {
        // Create the layer only once and store it in ref
        featureLayer = new FeatureLayer({
          id: 'enquiries-feature-layer',
          url: getFeatureServerUrl('Enquiries'),
          outFields: ['objectid'],
          minScale: MapSettings.shapeMinScale,
          popupTemplate,
          visible: false // Start hidden, will be made visible after filter is applied
        });

        // Store the layer instance in ref to persist across map reloads
        featureLayerRef.current = featureLayer;

        // Watch for definitionExpression changes to ensure layer becomes visible when filters are applied
        featureLayer.watch('definitionExpression', (newValue: string) => {
          if (newValue && !featureLayer.visible) {
            featureLayer.visible = true;
          }
        });
      } else if (!featureLayer && featureLayerRef.current) {
        // Layer was created before but not added to map (after dual loading), use the stored instance
        featureLayer = featureLayerRef.current;
      }

      // Always update popupTemplate to ensure it uses the latest clickDetailButton closure
      if (featureLayer) {
        featureLayer.popupTemplate = popupTemplate;
      }

      // Try to add the layer if it's not already in the map
      if (featureLayer && !findLayerById('enquiries-feature-layer')) {
        addLayerToMap(featureLayer);
      }

      loadedView.popup.visibleElements = {
        featureNavigation: false,
        closeButton: false
      };
      loadedView.popup.actions = null;
      loadedView.popup.dockOptions = {
        buttonEnabled: false,
        breakpoint: {
          width: MD
        },
        position: 'bottom-center'
      };

      const calculatePopupAlignment = () => {
        let popupPos = 'auto';

        const popupRectBoundary = loadedView.popup.domNode.getBoundingClientRect();
        const finalPopupXPos = popupRectBoundary.x + popupRectBoundary.width;

        const popupY = loadedView.popup?.location?.y;
        const mapY = loadedView.center?.y;
        const popupX = loadedView.popup?.location?.x;
        const mapX = loadedView.center?.x;

        const minX: number = loadedView.position[0];
        const maxX: number = loadedView.size[0];

        if (popupY && mapY) {
          popupPos = popupY > mapY ? 'bottom-center' : 'top-center';
        }

        if (popupPos === 'bottom-center') {
          popupPos =
            popupX < mapX
              ? popupRectBoundary.x < minX
                ? 'bottom-right'
                : 'bottom-center'
              : finalPopupXPos > maxX
              ? 'bottom-left'
              : 'bottom-center';
        }

        if (popupPos === 'top-center') {
          popupPos =
            popupX < mapX
              ? popupRectBoundary.x < minX
                ? 'top-right'
                : 'auto'
              : 'auto';
        }

        return popupPos;
      };

      loadedView.popup.alignment = () => calculatePopupAlignment();

      setIsMapLoaded(true);
    }
  };

  const setDefinitionExpression = useCallback(
    (filter: EnquirySearchFilters, userId: number) => {
      const { loadedView, loadedMapModules } = window;

      // Use centralized utility to find layer from the correct map instance
      const featureLayer: any = findLayerById('enquiries-feature-layer');

      if (featureLayer && loadedView && loadedMapModules) {
        const filterArr = Object.entries(filter);
        const definitionExpression = filterArr.reduce(
          filterStringReducer,
          !isFullSet ? `user_id = ${userId}` : ''
        );

        loadedView?.popup && loadedView.closePopup();
        dispatch(removeAlertByGroup('map-alert'));

        // Apply filter BEFORE making layer visible to prevent CPU spike from loading all records
        featureLayer.definitionExpression = definitionExpression;
        featureLayer.visible = true;

        if (!mapSearchWithExtentZoomRef.current) {
          const { Polygon } = loadedMapModules;
          dispatch(
            queryFeatureLayerExtentStart(
              featureLayer,
              definitionExpression,
              Polygon.fromExtent(loadedView.extent)
            )
          );
        } else {
          dispatch(queryFeatureLayerExtentStart(featureLayer));
        }
      }
    },
    [dispatch, isFullSet]
  );

  useEffect(() => {
    if (enquiriesLayerExtent) {
      if (typeof enquiriesLayerExtent !== 'string') {
        if (enquiriesLayerExtent?.noRecord) {
          dispatch(
            addAlert('map-alert', 'info', 'No record found', '', {
              id: 'map-alert'
            })
          );
        } else {
          // Only zoom if user explicitly clicked zoom button, not on initial load
          if (mapSearchWithExtentZoomRef.current && !isInitialLoadRef.current) {
            zoomToFeature(enquiriesLayerExtent);
          }
          // Mark that initial load is complete after first extent is received
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        }
      }
    }
  }, [enquiriesLayerExtent, dispatch]);

  useEffect(() => {
    mapSearchWithExtentZoomRef.current = mapSearchWithExtentZoom[0];
    user?.id &&
      isMapLoaded &&
      isMapVisible &&
      searchFilter &&
      setDefinitionExpression(searchFilter, user.id);
  }, [
    user,
    isMapLoaded,
    isMapVisible,
    searchFilter,
    setDefinitionExpression,
    mapSearchWithExtentZoom
  ]);

  useEffect(() => {
    if (isMapLoaded && baseMapTheme) {
      const { polygonDarkSymbol, polygonSymbol } = MapSettings;

      // Use centralized utility to find layer from the correct map instance
      const layer: any = findLayerById('enquiries-feature-layer');
      if (layer) {
        layer.renderer = {
          type: 'simple',
          symbol: baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol
        };
      }
    }
  }, [baseMapTheme, isMapLoaded]);

  useComponentMount(() => {
    dispatch(removeAlertByGroup('map-alert'));
    return () => {
      dispatch(removeAlertByGroup('map-alert'));
      dispatch(clearFeatureLayerLayerExtent());
      featureLayerRef.current = null; // Clean up ref on unmount
    };
  });

  return (
    <div
      className={`history-page-map--view ${
        isPanelExpanded ? 'panel-expanded' : ''
      }`}
      data-testid="enqiryHistoryMapView"
    >
      <Map
        extraModules={extraMapModules}
        onReady={onMapReady}
        showSearchButton={true}
        showbaseMapButton={true}
      />
    </div>
  );
};

const mapStateToProps = ({
  mapCache,
  session,
  enquiresCache,
  app
}: RootState) => ({
  enquiriesLayerExtent: mapCache.featureLayerExtent,
  searchFilter: enquiresCache.data.filters,
  user: session?.currentUser,
  baseMapTheme: app?.baseMapTheme,
  mapSearchWithExtentZoom: mapCache.mapSearchWithExtentZoom
});

const connector = connect(mapStateToProps);

type ownProps = {
  isFullSet: boolean | null | undefined;
  selectedId: (val: number) => void;
  isMapVisible: boolean;
  isPanelExpanded: boolean;
};

type Props = CFCProps<typeof connector> & ownProps;

MapView.displayName = 'EnquiriesHistoryMapView';

export default connector(MapView);
